<!--
  Shared SVG filter defs rendered once per layout. Hidden at 0x0 so they
  contribute nothing to layout / paint beyond the filter registration.

  Exposed filters:
    #sfg-grain-chalk  — single-layer fractal-noise grain for chalk
                        surfaces. Paints black specks at ~8% alpha so
                        the cream page stays bright; adding a second
                        speckle layer on top of the chalk warmed the
                        page toward tan, so chalk deliberately gets a
                        lighter treatment than ink.
    #sfg-grain-ink    — soft grain tuned for dark surfaces. Paints
                        white specks at ~11% alpha so they register
                        against an ink background without reading as
                        stars.
    #sfg-speckle-ink  — sharp speckle for dark surfaces. White at
                        ~14% alpha. Dark surfaces continue to carry
                        the full two-layer T09 treatment because ink
                        needs the weight to feel like newsprint.
    #sfg-torn-edge    — displacement-maps edges by ~6 units for a
                        slightly hand-cut contour. Used by SfgPaperStack
                        and reserved for sticker border treatments.

  Applied by SfgSurfaceTexture, which drops one (chalk) or two (ink)
  absolute-positioned SVG rects filtered through these defs onto a
  surface. Text and buttons never receive the filters; only colored
  backgrounds.
-->
<template>
  <div
    aria-hidden="true"
    style="position: absolute; width: 0; height: 0; overflow: hidden; pointer-events: none;"
    v-html="filterMarkup"
  />
</template>

<script setup lang="ts">
// Authoring the filter defs in a string keeps SVG attribute casing
// (baseFrequency, numOctaves, feTurbulence) exact, regardless of how
// Vue's template compiler normalizes attribute names.
const filterMarkup = `
<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0">
  <defs>
    <filter id="sfg-grain-chalk" x="0%" y="0%" width="100%" height="100%">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/>
      <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.08 0"/>
    </filter>
    <filter id="sfg-grain-ink" x="0%" y="0%" width="100%" height="100%">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/>
      <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.11 0"/>
    </filter>
    <filter id="sfg-speckle-ink" x="0%" y="0%" width="100%" height="100%">
      <feTurbulence type="turbulence" baseFrequency="2.2" numOctaves="1" stitchTiles="stitch"/>
      <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.14 0"/>
    </filter>
    <filter id="sfg-torn-edge" x="-5%" y="-5%" width="110%" height="110%">
      <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="2" seed="4" result="noise"/>
      <feDisplacementMap in="SourceGraphic" in2="noise" scale="6"/>
    </filter>
  </defs>
</svg>
`.trim()
</script>
